export const AUDIO_PLAYBACK_GESTURE_PRIMER_DATA_URI = 'data:audio/wav;base64,UklGRrQBAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YZABAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA';
const AUDIO_PLAYBACK_GESTURE_PRIMER_TIMEOUT_MS = 500;
export const AUDIO_PLAYBACK_START_TIMEOUT_MS = 4000;

/**
 * Browser audio play promises can occasionally stay pending while source
 * metadata churns. Keep this primer best-effort so it cannot block real
 * playback startup.
 */
function waitForAudioPlaybackPrimer(playPromise: Promise<void>): Promise<void> {
    return new Promise((resolve) => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const finish = (): void => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            resolve();
        };

        timeoutId = setTimeout(finish, AUDIO_PLAYBACK_GESTURE_PRIMER_TIMEOUT_MS);
        playPromise.then(finish).catch(finish);
    });
}

/**
 * Some browser/media combinations leave `HTMLMediaElement.play()` pending
 * while metadata is being replaced. Treat the `playing` event as success, and
 * only fail when the media element itself reports an unrecoverable error.
 */
export function waitForAudioPlaybackStart(data: {
    audioElement: HTMLAudioElement,
    playPromise: Promise<void>,
}): Promise<void> {
    const {
        audioElement,
        playPromise,
    } = data;

    return new Promise((resolve, reject) => {
        let settled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const cleanup = (): void => {
            audioElement.removeEventListener('playing', handlePlaying);
            audioElement.removeEventListener('play', handlePlaying);
            audioElement.removeEventListener('timeupdate', handlePlaying);
            audioElement.removeEventListener('canplay', handlePlaying);
            audioElement.removeEventListener('error', handleError);
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };
        const finish = (): void => {
            if (settled) {
                return;
            }

            settled = true;
            cleanup();
            resolve();
        };
        const fail = (error: unknown): void => {
            if (settled) {
                return;
            }

            settled = true;
            cleanup();
            reject(error);
        };
        function handlePlaying(): void {
            finish();
        }
        function handleError(): void {
            fail(audioElement.error || new Error('Audio playback failed before it started.'));
        }
        const hasUnrecoverableMediaState = (): boolean => (
            audioElement.error != null
            || (
                audioElement.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
                && !audioElement.currentSrc
            )
        );

        audioElement.addEventListener('playing', handlePlaying);
        audioElement.addEventListener('play', handlePlaying);
        audioElement.addEventListener('timeupdate', handlePlaying);
        audioElement.addEventListener('canplay', handlePlaying);
        audioElement.addEventListener('error', handleError);
        timeoutId = setTimeout(() => {
            if (hasUnrecoverableMediaState()) {
                fail(audioElement.error || new Error('Audio playback failed before it started.'));
                return;
            }

            // A pending play() with a viable source usually means the browser is
            // still settling buffering/metadata, not that playback is impossible.
            // Resolve so callers can leave the transient "starting" state.
            finish();
        }, AUDIO_PLAYBACK_START_TIMEOUT_MS);

        playPromise.then(finish).catch(fail);
    });
}

/**
 * Safari and iOS can reject delayed `audio.play()` calls once an async fetch
 * has separated them from the original press gesture. Prime the target audio
 * element with a tiny silent clip during that gesture, then restore its prior
 * source state so the eventual real source can start without another tap.
 */
export async function primeAudioElementForAsyncPlaybackStart(data: {
    audioElement: HTMLAudioElement | null,
    syncAudioSource: (
        nextAudioUrl: string | null,
        nextMimeType: string | null | undefined,
        options?: {
            forceReload?: boolean,
        },
    ) => void,
}): Promise<void> {
    const {
        audioElement,
        syncAudioSource,
    } = data;

    if (!audioElement) {
        return;
    }

    const sourceElement = audioElement.querySelector('source') as HTMLSourceElement | null;
    const previousSourceUrl = sourceElement?.getAttribute('src') ?? null;
    const previousSourceMimeType = sourceElement?.getAttribute('type') ?? undefined;
    const previousMuted = audioElement.muted;
    const previousVolume = audioElement.volume;
    const previousPlaysInlineAttribute = audioElement.getAttribute('playsinline');
    const previousWebkitPlaysInlineAttribute = audioElement.getAttribute('webkit-playsinline');

    try {
        audioElement.setAttribute('playsinline', '');
        audioElement.setAttribute('webkit-playsinline', '');
        audioElement.muted = true;
        audioElement.volume = 0;
        syncAudioSource(AUDIO_PLAYBACK_GESTURE_PRIMER_DATA_URI, 'audio/wav', {
            forceReload: true,
        });
        await waitForAudioPlaybackPrimer(audioElement.play());
    } catch (_error) {
        // Best-effort primer only. If this fails we still fall back to the
        // existing playback startup path.
    } finally {
        audioElement.pause();
        audioElement.currentTime = 0;
        audioElement.muted = previousMuted;
        audioElement.volume = previousVolume;
        if (previousPlaysInlineAttribute == null) {
            audioElement.removeAttribute('playsinline');
        } else {
            audioElement.setAttribute('playsinline', previousPlaysInlineAttribute);
        }
        if (previousWebkitPlaysInlineAttribute == null) {
            audioElement.removeAttribute('webkit-playsinline');
        } else {
            audioElement.setAttribute('webkit-playsinline', previousWebkitPlaysInlineAttribute);
        }
        syncAudioSource(previousSourceUrl, previousSourceMimeType, {
            forceReload: true,
        });
    }
}
